import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { CreditCard, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

function RecordPaymentDialog({ trigger, onSuccess }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  
  const emptyForm = () => ({
    invoice_id: '',
    amount: '',
    payment_method: 'CASH',
    reference: '',
    notes: '',
    payer_name: '',
    payment_date: new Date().toISOString().slice(0, 10)
  });
  const [paymentForm, setPaymentForm] = useState(emptyForm());

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (selectedStudent) {
      fetchStudentInvoices(selectedStudent);
    }
  }, [selectedStudent]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch students
      const studentsResponse = await axios.get(`${API}/api/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(studentsResponse.data);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive'
      });
    }
  };

  const fetchStudentInvoices = async (studentId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch unpaid invoices for selected student
      const invoicesResponse = await axios.get(`${API}/api/invoices?student_id=${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter for unpaid invoices
      const unpaidInvoices = invoicesResponse.data.filter(inv => ['PENDING', 'PARTIAL', 'OVERDUE'].includes(inv.financial_status || inv.status));
      setInvoices(unpaidInvoices);
      
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.invoice_id || !paymentForm.amount) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner une facture et saisir un montant',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const { data: rec } = await axios.post(`${API}/api/payments/record`, paymentForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      try {
        const pdf = await axios.get(`${API}/api/payments/${rec.payment_id}/receipt`, { responseType: 'blob' });
        const url = URL.createObjectURL(pdf.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recu_${rec.receipt_number}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Receipt download failed', err);
      }

      toast({
        title: 'Succès',
        description: 'Paiement enregistré avec succès'
      });

      // Reset form
      setPaymentForm(emptyForm());
      setSelectedStudent('');
      setInvoices([]);
      setOpen(false);
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error recording payment:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de l\'enregistrement du paiement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Élève';
  };

  const formatCurrency = (amount) => `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <CreditCard className="w-4 h-4 mr-2" />
            Enregistrer un paiement
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Enregistrer un paiement manuel</DialogTitle>
          <DialogDescription>
            Enregistrez un paiement en espèces, par chèque ou par virement bancaire
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Sélection Élève */}
          <div className="space-y-2">
            <Label htmlFor="student">Élève *</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger data-testid="payment-student-select">
                <SelectValue placeholder="Sélectionnez un élève" />
              </SelectTrigger>
              <SelectContent>
                {students.map((student) => (
                  <SelectItem key={student.id} value={student.id}>
                    {student.first_name} {student.last_name}
                    {student.class_name && ` - ${student.class_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sélection Facture */}
          {selectedStudent && (
            <div className="space-y-2">
              <Label htmlFor="invoice">Facture *</Label>
              <Select 
                value={paymentForm.invoice_id} 
                onValueChange={(value) => {
                  const invoice = invoices.find(inv => inv.id === value);
                  setPaymentForm({ 
                    ...paymentForm, 
                    invoice_id: value,
                    amount: invoice?.balance ?? invoice?.amount ?? invoice?.total_amount ?? ''
                  });
                }}
              >
                <SelectTrigger data-testid="payment-invoice-select">
                  <SelectValue placeholder="Sélectionnez une facture" />
                </SelectTrigger>
                <SelectContent>
                  {invoices.length === 0 ? (
                    <SelectItem value="none" disabled>
                      Aucune facture impayée
                    </SelectItem>
                  ) : (
                    invoices.map((invoice) => (
                      <SelectItem key={invoice.id} value={invoice.id}>
                        {invoice.invoice_number ? `${invoice.invoice_number} · ` : ''}{invoice.title || invoice.description || 'Facture'} - reste {formatCurrency(invoice.balance ?? invoice.amount ?? invoice.total_amount)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Montant */}
          <div className="space-y-2">
            <Label htmlFor="amount">Montant (GNF) *</Label>
            <Input
              id="amount"
              data-testid="payment-amount-input"
              type="number"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              placeholder="50000"
              required
            />
          </div>

          {/* Méthode de paiement */}
          <div className="space-y-2">
            <Label htmlFor="payment_method">Méthode de paiement *</Label>
            <Select 
              value={paymentForm.payment_method} 
              onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
            >
              <SelectTrigger data-testid="payment-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">Espèces</SelectItem>
                <SelectItem value="CHECK">Chèque</SelectItem>
                <SelectItem value="BANK_TRANSFER">Virement bancaire</SelectItem>
                <SelectItem value="MOBILE_MONEY">Mobile money</SelectItem>
                <SelectItem value="OTHER">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="payer_name">Payeur</Label>
              <Input id="payer_name" data-testid="payment-payer-input" value={paymentForm.payer_name}
                onChange={(e) => setPaymentForm({ ...paymentForm, payer_name: e.target.value })} placeholder="Nom du payeur" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_date">Date d'encaissement</Label>
              <Input id="payment_date" data-testid="payment-date-input" type="date" value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
            </div>
          </div>

          {/* Référence */}
          <div className="space-y-2">
            <Label htmlFor="reference">Référence (optionnel)</Label>
            <Input
              id="reference"
              value={paymentForm.reference}
              onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
              placeholder="N° chèque, référence virement..."
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              placeholder="Notes additionnelles..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} data-testid="payment-submit-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer le paiement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RecordPaymentDialog;
